import {
  isAuthenticationFailure,
  isAuthorizationFailure,
} from '@/errorHandling'

export type ProgressCommitCallback = (
  currentTime: number,
  fileId: number
) => void | Promise<void>

export interface ProgressCommitter {
  commit: (currentTime: number, fileId?: number) => boolean
  seed: (currentTime: number, fileId?: number) => void
}

export const shouldRetryVideoProgressMutation = (
  failureCount: number,
  error: unknown
) =>
  failureCount < 2 &&
  !isAuthenticationFailure(error) &&
  !isAuthorizationFailure(error)

const normalizeProgress = (currentTime: number): number | null => {
  if (!Number.isFinite(currentTime)) return null

  const seconds = Math.floor(currentTime)
  return seconds > 0 ? seconds : null
}

export const createProgressCommitter = (
  onCommit: ProgressCommitCallback
): ProgressCommitter => {
  const lastCommittedByFile = new Map<number, number>()
  const inFlightByFile = new Map<number, number>()

  const seed = (currentTime: number, fileId?: number) => {
    const seconds = normalizeProgress(currentTime)
    if (fileId == null || seconds == null) return

    lastCommittedByFile.set(fileId, seconds)
  }

  const commit = (currentTime: number, fileId?: number) => {
    const seconds = normalizeProgress(currentTime)
    if (fileId == null || seconds == null) return false
    if (lastCommittedByFile.get(fileId) === seconds) return false
    if (inFlightByFile.get(fileId) === seconds) return false

    inFlightByFile.set(fileId, seconds)
    let commitResult: void | Promise<void>
    try {
      commitResult = onCommit(seconds, fileId)
    } catch {
      inFlightByFile.delete(fileId)
      return true
    }

    void Promise.resolve(commitResult)
      .then(() => {
        lastCommittedByFile.set(fileId, seconds)
      })
      .catch(() => {
        // Leave the position uncommitted so a later lifecycle event can retry it.
      })
      .finally(() => {
        if (inFlightByFile.get(fileId) === seconds) {
          inFlightByFile.delete(fileId)
        }
      })
    return true
  }

  return { commit, seed }
}
